/**
 * GlobalPlayer — v3.0.135 (Phase 2)
 * Fixed bottom bar shown site-wide when a queue is loaded.
 *
 *   [cover] [title / playlist] [prev][play][next] [shuffle][repeat][volume] [X close]
 */
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Repeat1,
  Volume2, VolumeX, X, ListMusic, Music, ExternalLink,
} from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';

const fmt = (sec) => {
  if (!Number.isFinite(sec) || sec <= 0) return '0:00';
  const total = Math.floor(sec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export default function GlobalPlayer() {
  const {
    audioRef, queue, queueTitle, playlistId, current,
    playing, shuffle, repeatMode, volume, currentTime, duration, barVisible,
    togglePlay, next, prev, setShuffle, cycleRepeat, setVolume, seek, stop, hideBar,
    handleEnded, setCurrentTime, setDuration, setPlaying,
  } = usePlayer();

  const onTimeUpdate = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    setCurrentTime(el.currentTime || 0);
  }, [audioRef, setCurrentTime]);

  const onLoadedMetadata = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    setDuration(el.duration || 0);
  }, [audioRef, setDuration]);

  const onSeek = (e) => {
    const pct = parseFloat(e.target.value);
    if (duration > 0) seek((pct / 100) * duration);
  };

  const RepeatIcon = repeatMode === 'one' ? Repeat1 : Repeat;
  const repeatActive = repeatMode !== 'off';

  // Audio element is always mounted (even when bar is hidden) so
  // programmatic play() calls elsewhere still work; but we render the
  // bar UI only when there's a queue AND the user hasn't dismissed it.
  const showBar = barVisible && queue.length > 0;

  // v3.0.135 — Body padding when bar visible so page content isn't hidden.
  React.useEffect(() => {
    const cls = 'has-global-player';
    if (showBar) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [showBar]);

  return (
    <>
      <audio
        ref={audioRef}
        data-testid="global-player-audio"
        onEnded={handleEnded}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadedMetadata}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onError={() => {
          setPlaying(false);
          next();
        }}
      />
      {showBar && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-zinc-950/95 backdrop-blur-md border-t border-white/10"
          data-testid="global-player-bar"
        >
          {/* Progress bar sitting on top edge — the range input is
              hit-target enlarged on hover; the emerald fill remains the
              visual indicator. */}
          <div className="w-full h-1 bg-white/5 relative group/seek">
            <div
              className="h-full bg-emerald-400 transition-[width] duration-100 relative"
              style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
            >
              <span
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 bg-emerald-300 rounded-full opacity-0 group-hover/seek:opacity-100 transition-opacity shadow-[0_0_0_2px_rgba(0,0,0,0.5)]"
              />
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={0.1}
              value={duration > 0 ? (currentTime / duration) * 100 : 0}
              onChange={onSeek}
              className="absolute inset-x-0 top-1/2 -translate-y-1/2 w-full h-4 opacity-0 cursor-pointer"
              aria-label="Seek"
              data-testid="global-player-seek"
            />
          </div>
          <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-3">
            {/* Song info */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {current?.image_url ? (
                <img
                  src={current.image_url}
                  alt=""
                  className="w-12 h-12 rounded object-cover shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center shrink-0">
                  <Music size={20} className="text-zinc-500" />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-white truncate" data-testid="global-player-title">
                  {current?.title || 'Untitled'}
                </div>
                <div className="text-xs text-zinc-500 flex items-center gap-1.5 truncate">
                  {queueTitle && (
                    <>
                      <ListMusic size={11} className="shrink-0" />
                      {playlistId ? (
                        <Link
                          to={`/playlist/${playlistId}`}
                          className="hover:text-white hover:underline truncate inline-flex items-center gap-1"
                        >
                          {queueTitle} <ExternalLink size={10} />
                        </Link>
                      ) : (
                        <span className="truncate">{queueTitle}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Transport controls */}
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setShuffle()}
                title={shuffle ? 'Shuffle on' : 'Shuffle off'}
                className={`p-2 rounded transition-colors ${
                  shuffle ? 'text-emerald-400' : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="global-player-shuffle"
              >
                <Shuffle size={18} />
              </button>
              <button
                type="button"
                onClick={prev}
                className="p-2 rounded text-white hover:bg-white/10"
                data-testid="global-player-prev"
              >
                <SkipBack size={20} />
              </button>
              <button
                type="button"
                onClick={togglePlay}
                className="p-2 rounded-full bg-emerald-500 text-black hover:bg-emerald-400"
                data-testid="global-player-play"
              >
                {playing ? <Pause size={20} /> : <Play size={20} />}
              </button>
              <button
                type="button"
                onClick={next}
                className="p-2 rounded text-white hover:bg-white/10"
                data-testid="global-player-next"
              >
                <SkipForward size={20} />
              </button>
              <button
                type="button"
                onClick={cycleRepeat}
                title={`Repeat: ${repeatMode}`}
                className={`p-2 rounded transition-colors ${
                  repeatActive ? 'text-emerald-400' : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="global-player-repeat"
              >
                <RepeatIcon size={18} />
              </button>
            </div>

            {/* Time + volume */}
            <div className="hidden sm:flex items-center gap-3 min-w-0 shrink-0 pl-2">
              <div className="text-[11px] text-zinc-500 font-mono w-20 text-right">
                {fmt(currentTime)} / {fmt(duration)}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setVolume(volume > 0 ? 0 : 0.9)}
                  className="text-zinc-400 hover:text-white"
                  data-testid="global-player-mute"
                >
                  {volume > 0 ? <Volume2 size={16} /> : <VolumeX size={16} />}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  className="w-20 accent-emerald-400"
                  aria-label="Volume"
                  data-testid="global-player-volume"
                />
              </div>
            </div>

            {/* Close */}
            <button
              type="button"
              onClick={() => { stop(); hideBar(); }}
              className="p-1.5 rounded text-zinc-500 hover:text-white hover:bg-white/10 shrink-0"
              title="Close player"
              data-testid="global-player-close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
